import React, { Component } from 'react';

class EventNavigatorFlow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      answers: { ...(props.defaults || {}) },
      currentStepIndex: 0,
      recommendation: null,
      stage: 'questions',
      presetName: '',
      showSaveOverlay: false,
      saveState: 'idle'
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handlePresetNameChange = this.handlePresetNameChange.bind(this);
    this.handlePresetSave = this.handlePresetSave.bind(this);
    this.handleAuthSaveStart = this.handleAuthSaveStart.bind(this);
    this.handleOpenSaveOverlay = this.handleOpenSaveOverlay.bind(this);
    this.handleCloseSaveOverlay = this.handleCloseSaveOverlay.bind(this);
  }

  componentDidMount() {
    this.restoreDraft();
    this.emitProgress();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (
      prevState.currentStepIndex !== this.state.currentStepIndex ||
      prevState.stage !== this.state.stage
    ) {
      this.emitProgress();
    }
  }

  i18nValue(key) {
    if (this.props.i18n && this.props.i18n[key] !== undefined) {
      return this.props.i18n[key];
    }

    return EventNavigatorFlow.defaultProps.i18n[key];
  }

  steps() {
    return Array.isArray(this.props.questions) ? this.props.questions : [];
  }

  activeFields(step) {
    return (step?.fields || []).filter((field) => this.fieldVisible(field));
  }

  fieldVisible(field) {
    const conditions = field.visible_when || {};

    return Object.entries(conditions).every(([key, expectedValues]) => {
      const expected = Array.isArray(expectedValues) ? expectedValues : [expectedValues];
      return expected.map(String).includes(String(this.state.answers[key] || ''));
    });
  }

  currentStep() {
    return this.steps()[this.state.currentStepIndex] || null;
  }

  currentFields() {
    return this.activeFields(this.currentStep());
  }

  fieldValue(field) {
    const value = this.state.answers[field.key];
    if (field.type === 'checkbox') {
      return String(value) === '1';
    }

    return value === undefined || value === null ? '' : value;
  }

  handleFieldChange(field, event) {
    const value = field.type === 'checkbox' ? (event.target.checked ? '1' : '0') : event.target.value;

    this.setState({
      answers: {
        ...this.state.answers,
        [field.key]: value
      }
    });
  }

  validateCurrentStep() {
    const invalidField = this.currentFields().find((field) => {
      if (field.type === 'checkbox') {
        return false;
      }

      return String(this.state.answers[field.key] || '').trim() === '';
    });

    if (!invalidField) {
      return true;
    }

    this.setState({ invalidFieldKey: invalidField.key });
    return false;
  }

  handleNext() {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setState({
      currentStepIndex: Math.min(this.state.currentStepIndex + 1, this.steps().length - 1),
      invalidFieldKey: null
    });
  }

  handlePrevious() {
    this.setState({
      currentStepIndex: Math.max(this.state.currentStepIndex - 1, 0),
      invalidFieldKey: null
    });
  }

  csrfToken() {
    if (typeof document === 'undefined') {
      return '';
    }

    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
  }

  async requestRecommendation() {
    const response = await fetch(this.props.recommendationPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({ event_navigator: this.state.answers })
    });

    if (!response.ok) {
      throw new Error(`Event recommendation failed with status ${response.status}`);
    }

    return response.json();
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.validateCurrentStep()) {
      return;
    }

    this.setState({ saveState: 'loading' });

    try {
      const recommendation = await this.requestRecommendation();
      this.setState({
        recommendation: recommendation,
        stage: 'result',
        saveState: 'idle'
      });
    } catch (_error) {
      this.setState({ saveState: 'idle' });
    }
  }

  handleBack() {
    this.setState({
      stage: 'questions',
      currentStepIndex: Math.max(this.steps().length - 1, 0),
      saveState: 'idle'
    });
  }

  emitProgress() {
    if (typeof this.props.onProgressChange !== 'function') {
      return;
    }

    const totalSteps = this.steps().length + 1;
    const currentStep = this.state.stage === 'result' ? totalSteps : this.state.currentStepIndex + 1;
    this.props.onProgressChange({
      currentStep: currentStep,
      totalSteps: totalSteps,
      progressPercent: Math.round((currentStep / Math.max(totalSteps, 1)) * 100)
    });
  }

  currentReturnTo() {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    return `${window.location.pathname}${window.location.search || ''}`;
  }

  persistDraftForAuth() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    window.localStorage.setItem('audiocrowd.eventNavigatorDraft', JSON.stringify({
      answers: this.state.answers,
      recommendation: this.state.recommendation
    }));
  }

  restoreDraft() {
    if (!this.props.customerSignedIn || typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const rawDraft = window.localStorage.getItem('audiocrowd.eventNavigatorDraft');
    if (!rawDraft) {
      return;
    }

    try {
      const draft = JSON.parse(rawDraft);
      if (draft.answers && draft.recommendation) {
        this.setState({ answers: draft.answers, recommendation: draft.recommendation, stage: 'result' });
      }
      window.localStorage.removeItem('audiocrowd.eventNavigatorDraft');
    } catch (_error) {
      window.localStorage.removeItem('audiocrowd.eventNavigatorDraft');
    }
  }

  presetPayload() {
    const recommendation = this.state.recommendation || {};

    return {
      name: this.state.presetName.trim(),
      answers: this.state.answers,
      estimated_price_cents: recommendation.pricing_quote?.price_cents || 0,
      recommendation: recommendation
    };
  }

  async submitPreset({ authMode = null } = {}) {
    const response = await fetch(this.props.savePresetPath, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json'
      },
      body: JSON.stringify({
        preset: this.presetPayload(),
        auth_mode: authMode,
        return_to: this.currentReturnTo()
      })
    });

    if (!response.ok) {
      throw new Error(`Event preset save failed with status ${response.status}`);
    }

    return response.json();
  }

  handlePresetNameChange(event) {
    this.setState({ presetName: event.target.value, saveState: 'idle' });
  }

  handleOpenSaveOverlay() {
    this.setState({ showSaveOverlay: true });
  }

  handleCloseSaveOverlay() {
    this.setState({ showSaveOverlay: false });
  }

  async handlePresetSave() {
    if (!this.state.presetName.trim()) {
      return;
    }

    this.setState({ saveState: 'saving' });

    try {
      const result = await this.submitPreset();
      if (result.status === 'saved' && typeof window !== 'undefined') {
        window.localStorage.removeItem('audiocrowd.eventNavigatorDraft');
      }
      this.setState({ saveState: 'saved', showSaveOverlay: false });
    } catch (_error) {
      this.setState({ saveState: 'idle' });
    }
  }

  async handleAuthSaveStart(authMode) {
    this.persistDraftForAuth();
    this.setState({ saveState: 'saving' });

    try {
      const result = await this.submitPreset({ authMode: authMode });
      if (result.status === 'auth_required' && result.redirect_to && typeof window !== 'undefined') {
        window.location.assign(result.redirect_to);
      }
    } catch (_error) {
      this.setState({ saveState: 'idle' });
    }
  }

  renderField(field) {
    const value = this.fieldValue(field);
    const invalid = this.state.invalidFieldKey === field.key;

    return (
      <div className='smart-navigator-flow__event-field' key={field.key}>
        {field.type === 'checkbox' ? (
          <label className='smart-navigator-flow__event-checkbox'>
            <input type='checkbox' checked={value} onChange={(event) => this.handleFieldChange(field, event)} />
            <span>{field.label}</span>
          </label>
        ) : (
          <label className='smart-navigator-flow__event-label'>
            <span>{field.label}</span>
            {field.type === 'number' ? (
              <input
                type='number'
                min={field.min}
                max={field.max}
                value={value}
                className={invalid ? 'input--invalid' : ''}
                onChange={(event) => this.handleFieldChange(field, event)}
              />
            ) : (
              <select value={value} className={invalid ? 'input--invalid' : ''} onChange={(event) => this.handleFieldChange(field, event)}>
                {field.options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
            )}
          </label>
        )}
        {field.hint ? <small className='smart-navigator-flow__event-hint'>{field.hint}</small> : null}
      </div>
    );
  }

  renderQuestions() {
    const step = this.currentStep();
    if (!step) {
      return null;
    }

    const isLastStep = this.state.currentStepIndex === this.steps().length - 1;

    return (
      <div className='smart-navigator-flow__branch-layout'>
        <div className='smart-navigator-flow__panel smart-navigator-flow__panel--question'>
          <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('events_title')}</div>
          <h2 className='smart-navigator-flow__title'>{step.title}</h2>
          <p className='smart-navigator-flow__copy'>{step.copy}</p>
          <div className='smart-navigator-flow__event-fields'>
            {this.activeFields(step).map((field) => this.renderField(field))}
          </div>
          <div className='smart-navigator-flow__actions'>
            {this.state.currentStepIndex > 0 ? (
              <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handlePrevious}>{this.i18nValue('back')}</button>
            ) : (
              <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.props.onBack}>{this.i18nValue('back_to_selection')}</button>
            )}
            {isLastStep ? (
              <button type='button' className='license-navigator-v2-demo__cta' onClick={this.handleSubmit} disabled={this.state.saveState === 'loading'}>
                {this.state.saveState === 'loading' ? this.i18nValue('loading') : this.i18nValue('show_price')}
              </button>
            ) : (
              <button type='button' className='license-navigator-v2-demo__cta' onClick={this.handleNext}>{this.i18nValue('next')}</button>
            )}
          </div>
        </div>
        <aside className='smart-navigator-flow__panel smart-navigator-flow__panel--status'>
          <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('status_eyebrow')}</div>
          <div className='smart-navigator-flow__status-title'>{this.i18nValue('event_status_title')}</div>
          <p className='smart-navigator-flow__status-copy'>{this.i18nValue('event_status_copy')}</p>
          <div className='smart-navigator-flow__event-step'>{`${this.state.currentStepIndex + 1} / ${this.steps().length}`}</div>
        </aside>
      </div>
    );
  }

  formatPrice(cents) {
    return new Intl.NumberFormat(this.props.locale === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format((cents || 0) / 100);
  }

  renderResult() {
    const recommendation = this.state.recommendation || {};
    const quote = recommendation.pricing_quote || {};
    const notes = Array.isArray(recommendation.notes) ? recommendation.notes : [];
    const summaryItems = [
      [this.i18nValue('license_type'), recommendation.recommended_license_type_label],
      [this.i18nValue('audience'), recommendation.audience_label],
      [this.i18nValue('locations'), recommendation.occurrences],
      [this.i18nValue('duration'), recommendation.days],
      [this.i18nValue('video_usage'), recommendation.video_usage_level_label]
    ].filter((item) => item[1] !== undefined && item[1] !== null);

    return (
      <div className='smart-navigator-flow__branch-layout'>
        <div className='smart-navigator-flow__panel smart-navigator-flow__panel--result'>
          <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('events_title')}</div>
          <h2 className='smart-navigator-flow__title'>{this.i18nValue('event_result_title')}</h2>
          <p className='smart-navigator-flow__copy'>{recommendation.pricing_text || this.i18nValue('event_result_copy')}</p>
          {quote.price_cents ? <div className='smart-navigator-flow__event-price'>{this.formatPrice(quote.price_cents)}</div> : null}
          {recommendation.pricing_note ? <p className='smart-navigator-flow__copy'>{recommendation.pricing_note}</p> : null}
          <dl className='smart-navigator-flow__status-list'>
            {summaryItems.map(([label, value]) => (
              <div className='smart-navigator-flow__status-item' key={label}>
                <dt className='smart-navigator-flow__status-item-label'>{label}</dt>
                <dd className='smart-navigator-flow__status-item-value'>{value}</dd>
              </div>
            ))}
          </dl>
          {notes.length > 0 ? <ul className='smart-navigator-flow__event-notes'>{notes.map((note) => <li key={note}>{note}</li>)}</ul> : null}
          <div className='smart-navigator-flow__actions'>
            <a className='license-navigator-v2-demo__cta' href={recommendation.event_track_search_path}>{this.i18nValue('select_tracks')}</a>
            <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handleBack}>{this.i18nValue('edit_configuration')}</button>
          </div>
          {this.props.customerSignedIn ? this.renderSaveActions() : this.renderAuthNotice()}
          {this.renderSaveOverlay()}
        </div>
        <aside className='smart-navigator-flow__panel smart-navigator-flow__panel--status'>
          <div className='smart-navigator-flow__eyebrow'>{this.i18nValue('status_eyebrow')}</div>
          <div className='smart-navigator-flow__status-title'>{this.i18nValue('event_ready_title')}</div>
          <p className='smart-navigator-flow__status-copy'>{this.i18nValue('event_ready_copy')}</p>
          {recommendation.event_track_pricing_path ? <a className='smart-navigator-flow__text-link' href={recommendation.event_track_pricing_path}>{this.i18nValue('pricing_link')}</a> : null}
        </aside>
      </div>
    );
  }

  renderSaveActions() {
    return (
      <div className='smart-navigator-flow__actions smart-navigator-flow__actions--save'>
        <button type='button' className='license-navigator-v2-demo__cta' onClick={() => this.handleOpenSaveOverlay()} disabled={this.state.saveState === 'saved'}>
          {this.state.saveState === 'saved' ? this.i18nValue('save_done') : this.i18nValue('save_configuration')}
        </button>
      </div>
    );
  }

  renderAuthNotice() {
    return (
      <div className='smart-navigator-flow__auth-box'>
        <div className='smart-navigator-flow__event-section-title'>{this.i18nValue('save_configuration')}</div>
        <p className='smart-navigator-flow__copy'>{this.i18nValue('save_requires_account')}</p>
        <div className='smart-navigator-flow__actions'>
          <button type='button' className='license-navigator-v2-demo__cta' onClick={() => this.handleAuthSaveStart('sign_in')}>{this.i18nValue('sign_in_to_save')}</button>
          <button type='button' className='license-navigator-v2-demo__ghost' onClick={() => this.handleAuthSaveStart('sign_up')}>{this.i18nValue('create_account_to_save')}</button>
        </div>
      </div>
    );
  }

  renderSaveOverlay() {
    if (!this.props.customerSignedIn || !this.state.showSaveOverlay) {
      return null;
    }

    return (
      <div className='license-navigator-v2-flow__overlay' role='dialog' aria-modal='true'>
        <button type='button' className='license-navigator-v2-flow__overlay-backdrop' onClick={this.handleCloseSaveOverlay} aria-label={this.i18nValue('close_save_overlay')}></button>
        <div className='license-navigator-v2-flow__overlay-card'>
          <div className='license-navigator-v2-flow__result-section-title'>{this.i18nValue('save_configuration')}</div>
          <p className='license-navigator-v2-flow__result-copy'>{this.i18nValue('save_overlay_copy')}</p>
          <label className='license-navigator-v2-flow__overlay-label' htmlFor='event-navigator-preset-name'>{this.i18nValue('save_overlay_label')}</label>
          <input id='event-navigator-preset-name' type='text' className='license-navigator-v2-flow__input' value={this.state.presetName} onChange={this.handlePresetNameChange} placeholder={this.i18nValue('save_overlay_placeholder')} />
          <div className='license-navigator-v2-flow__actions'>
            <button type='button' className='license-navigator-v2-demo__cta' onClick={this.handlePresetSave} disabled={!this.state.presetName.trim() || this.state.saveState === 'saving'}>{this.i18nValue('save_overlay_confirm')}</button>
            <button type='button' className='license-navigator-v2-demo__ghost' onClick={this.handleCloseSaveOverlay}>{this.i18nValue('save_overlay_cancel')}</button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return this.state.stage === 'result' ? this.renderResult() : this.renderQuestions();
  }
}

EventNavigatorFlow.defaultProps = {
  locale: 'de',
  defaults: {},
  questions: [],
  customerSignedIn: false,
  recommendationPath: null,
  savePresetPath: null,
  i18n: {
    events_title: 'Events',
    back_to_selection: '← Zurück',
    back: 'Zurück',
    next: 'Weiter',
    show_price: 'Preis anzeigen',
    loading: 'Preis wird berechnet …',
    status_eyebrow: 'Ihr aktueller Stand',
    event_status_title: 'Event-Konfiguration',
    event_status_copy: 'Wir führen Sie durch die Angaben, die den Event-Track-Preis bestimmen.',
    event_result_title: 'Ihre Event-Track-Empfehlung',
    event_result_copy: 'Auf Basis Ihrer Angaben können Sie jetzt die passenden Titel auswählen.',
    event_ready_title: 'Nächster Schritt',
    event_ready_copy: 'Wählen Sie jetzt Ihre Titel aus. Die Event-Konfiguration wird anschließend für die Auswahl verwendet.',
    select_tracks: 'Titel für das Event auswählen',
    pricing_link: 'Event-Track-Preise ansehen',
    license_type: 'Lizenztyp',
    audience: 'Besucherzahl',
    locations: 'Veranstaltungen / Locations',
    duration: 'Laufzeit',
    video_usage: 'Video-Nutzung nach dem Event',
    edit_configuration: 'Konfiguration ändern',
    save_configuration: 'Event-Konfiguration speichern',
    save_done: 'Event-Konfiguration gespeichert',
    save_requires_account: 'Zum Speichern dieser Event-Konfiguration ist ein Kundenkonto erforderlich.',
    sign_in_to_save: 'Einloggen und speichern',
    create_account_to_save: 'Registrieren und speichern',
    save_overlay_copy: 'Geben Sie Ihrer Event-Konfiguration einen Namen, damit Sie sie später wiederverwenden können.',
    save_overlay_label: 'Name Ihrer Event-Konfiguration',
    save_overlay_placeholder: 'z. B. Sommerfest 2026',
    save_overlay_confirm: 'Event-Konfiguration speichern',
    save_overlay_cancel: 'Abbrechen',
    close_save_overlay: 'Overlay schließen'
  }
};

export default EventNavigatorFlow;
